import type { DAGEdge, DAGNode } from "../../api/artifacts";
import type {
  DagPathState,
  DagRecordType,
  DagViewerEdge,
  DagViewerGraph,
  DagViewerNode,
} from "./types";

/** The product-level views supported by the lineage workspace. */
export type DagViewMode =
  | "chronicle"
  | "flow"
  | "focus"
  | "impact"
  | "compare"
  | "time_compare"
  | "summary"
  | "dsm"
  | "runs"
  | "audit";

export type DagDirection = "upstream" | "both" | "downstream";

export type DagSummaryGroup = "category" | "kind" | "type" | "state" | "actor";

export interface DagFocusProjection {
  graph: DagViewerGraph;
  /** The canonical graph after applying the projection's effective filter mask. */
  analysisGraph: DagViewerGraph;
  /** Projected node index -> index in the immutable source graph. */
  sourceIndices: number[];
  /** Shortest directed distance from the anchor, keyed by record id. */
  distances: Map<string, number>;
  /** Unique records immediately beyond a finite traversal boundary. */
  boundaryCount: number;
}

export type DagCompareRole = "shared" | "a" | "b" | "anchor-a" | "anchor-b";

export interface DagCompareProjection {
  graph: DagViewerGraph;
  /** The canonical graph after applying the projection's effective filter mask. */
  analysisGraph: DagViewerGraph;
  sourceIndices: number[];
  roleById: Map<string, DagCompareRole>;
  /** Shared upstream records, excluding the two records being compared. */
  commonCount: number;
}

export type DagCategoryKeyByNode =
  | ReadonlyMap<string, string | null | undefined>
  | Readonly<Record<string, string | null | undefined>>
  | ((node: DagViewerNode, sourceIndex: number) => string | null | undefined);

export interface DagSummaryProjection {
  graph: DagViewerGraph;
  /** The canonical graph after applying the projection's effective filter mask. */
  analysisGraph: DagViewerGraph;
  memberIdsByGroup: Map<string, string[]>;
  /** Source record id -> virtual summary group id. */
  sourceToGroupId: Map<string, string>;
}

export interface DagProjectionCoverage {
  totalNodes: number;
  includedNodes: number;
  excludedNodes: number;
  ratio: number;
}

export interface DagRunMatrixRecord {
  nodeId: string;
  sourceIndex: number;
  familyId: string;
  familyLabel: string;
  familySource: string;
  runId: string;
  runLabel: string;
  runSource: string;
  sweepId: string | null;
  state: string | null;
  timestamp: string | null;
  parameters: Readonly<Record<string, unknown>>;
}

export interface DagRunMatrixCell {
  id: string;
  familyId: string;
  runId: string;
  records: DagRunMatrixRecord[];
  nodeIds: string[];
  states: string[];
  /** Null when records in the cell disagree or have no state. */
  state: string | null;
}

export interface DagRunMatrixRow {
  id: string;
  label: string;
  labels: string[];
  cells: DagRunMatrixCell[];
  recordCount: number;
}

export interface DagRunMatrixColumn {
  id: string;
  label: string;
  labels: string[];
  timestamp: string | null;
  recordCount: number;
}

export interface DagRunMatrixCoverage extends DagProjectionCoverage {
  nodesWithFamily: number;
  nodesWithRun: number;
  missingFamily: number;
  missingRun: number;
  missingBoth: number;
}

export interface DagRunMatrixModel {
  rows: DagRunMatrixRow[];
  columns: DagRunMatrixColumn[];
  records: DagRunMatrixRecord[];
  cellById: Map<string, DagRunMatrixCell>;
  excludedNodeIds: string[];
  coverage: DagRunMatrixCoverage;
}

export type DagAuditTimeSemantics = "event" | "recorded";

export interface DagAuditEvent {
  id: string;
  nodeId: string;
  sourceIndex: number;
  timestamp: string;
  timestampSource: string;
  timeSemantics: DagAuditTimeSemantics;
  eventType: string | null;
  label: string;
  actor: string | null;
  state: string | null;
}

export interface DagAuditCoverage extends DagProjectionCoverage {
  eventTimeNodes: number;
  recordedTimeNodes: number;
  invalidTimestampNodes: number;
  eventEntries: number;
  recordedEntries: number;
}

export interface DagAuditModel {
  events: DagAuditEvent[];
  coverage: DagAuditCoverage;
  untimedNodeIds: string[];
  invalidTimestampNodeIds: string[];
}

interface FieldMatch {
  value: string;
  source: string;
}

interface ValueSource {
  name: string;
  value: Record<string, unknown>;
}

function emptyGraph(): DagViewerGraph {
  return {
    nodes: [],
    edges: [],
    nodeIndexById: new Map(),
    incoming: [],
    outgoing: [],
    incidentEdges: [],
  };
}

function normalizedDepth(depth: number | null): number | null {
  if (depth == null || !Number.isFinite(depth)) return null;
  return Math.max(0, Math.floor(depth));
}

function assembleGraph(nodes: DagViewerNode[], edges: DagViewerEdge[]): DagViewerGraph {
  const nodeIndexById = new Map(nodes.map((node, index) => [node.id, index]));
  const incoming = Array.from({ length: nodes.length }, () => [] as number[]);
  const outgoing = Array.from({ length: nodes.length }, () => [] as number[]);
  const incidentEdges = Array.from({ length: nodes.length }, () => [] as number[]);

  edges.forEach((edge, edgeIndex) => {
    outgoing[edge.source]!.push(edge.target);
    incoming[edge.target]!.push(edge.source);
    incidentEdges[edge.source]!.push(edgeIndex);
    incidentEdges[edge.target]!.push(edgeIndex);
  });
  for (const list of [...incoming, ...outgoing]) list.sort((a, b) => a - b);

  return { nodes, edges, nodeIndexById, incoming, outgoing, incidentEdges };
}

/**
 * Build an induced subgraph while retaining the exact DagViewerNode objects
 * from the canonical graph. Labels and metadata therefore never pass through
 * normalization a second time.
 */
function inducedSubgraph(
  source: DagViewerGraph,
  requestedSourceIndices: Iterable<number>,
): { graph: DagViewerGraph; sourceIndices: number[] } {
  const sourceIndices = [...new Set(requestedSourceIndices)]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < source.nodes.length)
    .sort((a, b) => a - b);
  if (!sourceIndices.length) return { graph: emptyGraph(), sourceIndices };

  const projectedIndexBySource = new Map(sourceIndices.map((sourceIndex, index) => [sourceIndex, index]));
  const nodes = sourceIndices.map((sourceIndex) => source.nodes[sourceIndex]!);
  const edges: DagViewerEdge[] = [];

  for (const edge of source.edges) {
    const projectedSource = projectedIndexBySource.get(edge.source);
    const projectedTarget = projectedIndexBySource.get(edge.target);
    if (projectedSource == null || projectedTarget == null) continue;
    edges.push({
      id: edge.id,
      source: projectedSource,
      target: projectedTarget,
      kind: edge.kind,
      raw: edge.raw,
    });
  }

  return { graph: assembleGraph(nodes, edges), sourceIndices };
}

/**
 * Apply an effective filter mask before running an analytical projection.
 *
 * Traversal and aggregation must happen on this induced graph, rather than on
 * the full graph followed by a display-only mask. Otherwise hidden bridge
 * records can keep descendants reachable and hidden edges can leak into
 * summary counts. Source indices always refer to the immutable input graph.
 * A null or malformed mask is deliberately treated as no filter, matching the
 * renderer and table projection contracts.
 */
export function applyDagAnalysisMask(
  graph: DagViewerGraph,
  mask: Uint8Array | null = null,
): { graph: DagViewerGraph; sourceIndices: number[] } {
  const sourceIndices = graph.nodes.map((_, index) => index);
  if (!mask || mask.length !== graph.nodes.length) return { graph, sourceIndices };
  return inducedSubgraph(graph, sourceIndices.filter((index) => Boolean(mask[index])));
}

function directedDistances(
  adjacency: number[][],
  start: number,
  maxDepth: number | null,
): Map<number, number> {
  const distances = new Map<number, number>([[start, 0]]);
  const queue = [start];
  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head]!;
    const distance = distances.get(current)!;
    if (maxDepth != null && distance >= maxDepth) continue;
    for (const next of adjacency[current] ?? []) {
      if (distances.has(next)) continue;
      distances.set(next, distance + 1);
      queue.push(next);
    }
  }
  return distances;
}

function mergeShortest(target: Map<number, number>, source: Map<number, number>): void {
  for (const [index, distance] of source) {
    const current = target.get(index);
    if (current == null || distance < current) target.set(index, distance);
  }
}

function finiteBoundary(
  selected: ReadonlySet<number>,
  traversals: ReadonlyArray<{ distances: ReadonlyMap<number, number>; adjacency: number[][] }>,
  depth: number | null,
): number {
  if (depth == null) return 0;
  const boundary = new Set<number>();
  for (const traversal of traversals) {
    for (const [index, distance] of traversal.distances) {
      if (distance !== depth) continue;
      for (const next of traversal.adjacency[index] ?? []) {
        if (!selected.has(next)) boundary.add(next);
      }
    }
  }
  return boundary.size;
}

export function buildFocusProjection(
  graph: DagViewerGraph,
  anchorId: string,
  direction: DagDirection,
  depth: number | null,
  effectiveFilterMask: Uint8Array | null = null,
): DagFocusProjection {
  const analysis = applyDagAnalysisMask(graph, effectiveFilterMask);
  const analysisGraph = analysis.graph;
  const anchorIndex = analysisGraph.nodeIndexById.get(anchorId);
  if (anchorIndex == null) {
    return {
      graph: emptyGraph(),
      analysisGraph,
      sourceIndices: [],
      distances: new Map(),
      boundaryCount: 0,
    };
  }

  const boundedDepth = normalizedDepth(depth);
  const traversals: Array<{ distances: Map<number, number>; adjacency: number[][] }> = [];
  if (direction !== "downstream") {
    traversals.push({
      distances: directedDistances(analysisGraph.incoming, anchorIndex, boundedDepth),
      adjacency: analysisGraph.incoming,
    });
  }
  if (direction !== "upstream") {
    traversals.push({
      distances: directedDistances(analysisGraph.outgoing, anchorIndex, boundedDepth),
      adjacency: analysisGraph.outgoing,
    });
  }

  const sourceDistanceByIndex = new Map<number, number>();
  for (const traversal of traversals) mergeShortest(sourceDistanceByIndex, traversal.distances);
  // Both branches above always add a traversal, but retaining this fallback
  // makes the invariant explicit if DagDirection ever grows another value.
  sourceDistanceByIndex.set(anchorIndex, 0);

  const selected = new Set(sourceDistanceByIndex.keys());
  const subset = inducedSubgraph(analysisGraph, selected);
  const distances = new Map<string, number>();
  for (const analysisIndex of subset.sourceIndices) {
    distances.set(analysisGraph.nodes[analysisIndex]!.id, sourceDistanceByIndex.get(analysisIndex)!);
  }

  return {
    graph: subset.graph,
    analysisGraph,
    sourceIndices: subset.sourceIndices.map((analysisIndex) => analysis.sourceIndices[analysisIndex]!),
    distances,
    boundaryCount: finiteBoundary(selected, traversals, boundedDepth),
  };
}

export function buildCompareProjection(
  graph: DagViewerGraph,
  aId: string,
  bId: string,
  depth: number | null,
  effectiveFilterMask: Uint8Array | null = null,
): DagCompareProjection {
  const analysis = applyDagAnalysisMask(graph, effectiveFilterMask);
  const analysisGraph = analysis.graph;
  const boundedDepth = normalizedDepth(depth);
  const aIndex = analysisGraph.nodeIndexById.get(aId);
  const bIndex = analysisGraph.nodeIndexById.get(bId);
  const aLineage = aIndex == null
    ? new Map<number, number>()
    : directedDistances(analysisGraph.incoming, aIndex, boundedDepth);
  const bLineage = bIndex == null
    ? new Map<number, number>()
    : directedDistances(analysisGraph.incoming, bIndex, boundedDepth);
  const selected = new Set<number>([...aLineage.keys(), ...bLineage.keys()]);
  const subset = inducedSubgraph(analysisGraph, selected);
  const roleById = new Map<string, DagCompareRole>();
  let commonCount = 0;

  for (const analysisIndex of subset.sourceIndices) {
    const id = analysisGraph.nodes[analysisIndex]!.id;
    let role: DagCompareRole;
    if (analysisIndex === aIndex) role = "anchor-a";
    else if (analysisIndex === bIndex) role = "anchor-b";
    else if (aLineage.has(analysisIndex) && bLineage.has(analysisIndex)) {
      role = "shared";
      commonCount += 1;
    } else if (aLineage.has(analysisIndex)) role = "a";
    else role = "b";
    roleById.set(id, role);
  }

  return {
    graph: subset.graph,
    analysisGraph,
    sourceIndices: subset.sourceIndices.map((analysisIndex) => analysis.sourceIndices[analysisIndex]!),
    roleById,
    commonCount,
  };
}

function plainObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function displayString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function valueSources(node: DagViewerNode): ValueSource[] {
  return [
    { name: "record", value: node.raw as Record<string, unknown> },
    { name: "metadata", value: node.metadata },
    { name: "value_json", value: plainObject(node.raw.value_json) },
  ];
}

function findField(sources: ReadonlyArray<ValueSource>, keys: ReadonlyArray<string>): FieldMatch | null {
  for (const source of sources) {
    for (const key of keys) {
      const value = displayString(source.value[key]);
      if (value != null) return { value, source: `${source.name}.${key}` };
    }
  }
  return null;
}

function safeKey(value: string): string {
  try {
    return encodeURIComponent(value);
  } catch {
    return [...value].map((character) => character.codePointAt(0)!.toString(16)).join("-");
  }
}

function categoryFor(
  node: DagViewerNode,
  sourceIndex: number,
  categoryKeyByNode: DagCategoryKeyByNode | undefined,
): string | null {
  if (typeof categoryKeyByNode === "function") {
    return displayString(categoryKeyByNode(node, sourceIndex));
  }
  if (categoryKeyByNode && "get" in categoryKeyByNode && typeof categoryKeyByNode.get === "function") {
    return displayString(categoryKeyByNode.get(node.id));
  }
  if (categoryKeyByNode) {
    return displayString((categoryKeyByNode as Readonly<Record<string, string | null | undefined>>)[node.id]);
  }
  return findField(valueSources(node), ["category_key", "category"])?.value ?? null;
}

interface SummaryKey {
  value: string | null;
  token: string;
  label: string;
}

function summaryKey(
  node: DagViewerNode,
  sourceIndex: number,
  groupBy: DagSummaryGroup,
  categoryKeyByNode: DagCategoryKeyByNode | undefined,
): SummaryKey {
  let value: string | null;
  let missingLabel: string;
  switch (groupBy) {
    case "category":
      value = categoryFor(node, sourceIndex, categoryKeyByNode);
      missingLabel = "Uncategorised";
      break;
    case "kind":
      value = displayString(node.kind);
      missingLabel = "Unknown kind";
      break;
    case "type":
      value = displayString(node.type);
      missingLabel = "Unknown type";
      break;
    case "state":
      value = displayString(node.state);
      missingLabel = "Unknown state";
      break;
    case "actor":
      value = findField(valueSources(node), [
        "actor_name",
        "actor",
        "created_by_name",
        "created_by",
        "actor_id",
        "engineer_id",
        "user_id",
      ])?.value ?? null;
      missingLabel = "Unknown actor";
      break;
  }
  return value == null
    ? { value: null, token: "missing", label: missingLabel }
    : { value, token: `value:${value}`, label: value };
}

interface SummaryBucket {
  id: string;
  key: SummaryKey;
  sourceIndices: number[];
  internalEdgeCount: number;
  incomingEdgeCount: number;
  outgoingEdgeCount: number;
}

interface EdgeAggregate {
  sourceId: string;
  targetId: string;
  count: number;
  kinds: Map<string, number>;
}

function uniformValue<T>(values: ReadonlyArray<T>): T | null {
  if (!values.length) return null;
  const first = values[0]!;
  return values.every((value) => value === first) ? first : null;
}

function summaryPathState(nodes: ReadonlyArray<DagViewerNode>): DagPathState {
  return uniformValue(nodes.map((node) => node.pathState)) ?? "context";
}

function summaryRecordType(nodes: ReadonlyArray<DagViewerNode>): DagRecordType {
  return uniformValue(nodes.map((node) => node.type)) ?? "artifact";
}

export function buildSummaryProjection(
  graph: DagViewerGraph,
  groupBy: DagSummaryGroup,
  categoryKeyByNode?: DagCategoryKeyByNode,
  effectiveFilterMask: Uint8Array | null = null,
): DagSummaryProjection {
  const analysis = applyDagAnalysisMask(graph, effectiveFilterMask);
  const analysisGraph = analysis.graph;
  const bucketByToken = new Map<string, SummaryBucket>();
  const sourceToGroupId = new Map<string, string>();

  analysisGraph.nodes.forEach((node, analysisIndex) => {
    const sourceIndex = analysis.sourceIndices[analysisIndex]!;
    const key = summaryKey(node, sourceIndex, groupBy, categoryKeyByNode);
    let bucket = bucketByToken.get(key.token);
    if (!bucket) {
      bucket = {
        id: `summary:${groupBy}:${safeKey(key.token)}`,
        key,
        sourceIndices: [],
        internalEdgeCount: 0,
        incomingEdgeCount: 0,
        outgoingEdgeCount: 0,
      };
      bucketByToken.set(key.token, bucket);
    }
    bucket.sourceIndices.push(analysisIndex);
    sourceToGroupId.set(node.id, bucket.id);
  });

  const bucketById = new Map([...bucketByToken.values()].map((bucket) => [bucket.id, bucket]));

  const edgeAggregates = new Map<string, EdgeAggregate>();
  for (const edge of analysisGraph.edges) {
    const sourceNode = analysisGraph.nodes[edge.source];
    const targetNode = analysisGraph.nodes[edge.target];
    if (!sourceNode || !targetNode) continue;
    const sourceGroupId = sourceToGroupId.get(sourceNode.id);
    const targetGroupId = sourceToGroupId.get(targetNode.id);
    if (!sourceGroupId || !targetGroupId) continue;
    const sourceBucket = bucketById.get(sourceGroupId);
    const targetBucket = bucketById.get(targetGroupId);
    if (!sourceBucket || !targetBucket) continue;
    if (sourceGroupId === targetGroupId) {
      sourceBucket.internalEdgeCount += 1;
      continue;
    }
    sourceBucket.outgoingEdgeCount += 1;
    targetBucket.incomingEdgeCount += 1;
    const aggregateKey = `${sourceGroupId}\u0000${targetGroupId}`;
    let aggregate = edgeAggregates.get(aggregateKey);
    if (!aggregate) {
      aggregate = { sourceId: sourceGroupId, targetId: targetGroupId, count: 0, kinds: new Map() };
      edgeAggregates.set(aggregateKey, aggregate);
    }
    aggregate.count += 1;
    aggregate.kinds.set(edge.kind, (aggregate.kinds.get(edge.kind) ?? 0) + 1);
  }

  const buckets = [...bucketByToken.values()].sort((a, b) =>
    a.key.label.localeCompare(b.key.label) || a.id.localeCompare(b.id));
  const memberIdsByGroup = new Map<string, string[]>();
  const nodes: DagViewerNode[] = buckets.map((bucket) => {
    const members = bucket.sourceIndices.map((index) => analysisGraph.nodes[index]!);
    const memberIds = members.map((member) => member.id).sort((a, b) => a.localeCompare(b));
    memberIdsByGroup.set(bucket.id, memberIds);
    const metadata: Record<string, unknown> = {
      virtual: true,
      group_by: groupBy,
      group_key: bucket.key.value,
      member_count: members.length,
      internal_edge_count: bucket.internalEdgeCount,
      incoming_edge_count: bucket.incomingEdgeCount,
      outgoing_edge_count: bucket.outgoingEdgeCount,
    };
    const raw: DAGNode = {
      id: bucket.id,
      path: "",
      kind: `summary_${groupBy}`,
      name: bucket.key.label,
      type: "summary",
      metadata,
    };
    return {
      id: bucket.id,
      label: bucket.key.label,
      kind: `summary_${groupBy}`,
      type: summaryRecordType(members),
      semanticRole: "summary",
      state: uniformValue(members.map((member) => member.state)),
      timestamp: null,
      digest: null,
      channel: uniformValue(members.map((member) => member.channel)),
      anchor: members.some((member) => member.anchor),
      pathState: summaryPathState(members),
      metadata,
      raw,
    };
  });
  const nodeIndexById = new Map(nodes.map((node, index) => [node.id, index]));
  const edges: DagViewerEdge[] = [...edgeAggregates.values()]
    .sort((a, b) => a.sourceId.localeCompare(b.sourceId) || a.targetId.localeCompare(b.targetId))
    .map((aggregate) => {
      const relationshipCounts = Object.fromEntries(
        [...aggregate.kinds.entries()].sort(([a], [b]) => a.localeCompare(b)),
      );
      const kinds = Object.keys(relationshipCounts);
      const id = `summary-edge:${safeKey(aggregate.sourceId)}:${safeKey(aggregate.targetId)}`;
      const raw: DAGEdge = {
        id,
        parent_id: aggregate.sourceId,
        child_id: aggregate.targetId,
        relationship: kinds.length === 1 ? kinds[0] : "aggregated",
        metadata: {
          relationship_count: aggregate.count,
          relationship_counts: relationshipCounts,
        },
      };
      return {
        id,
        source: nodeIndexById.get(aggregate.sourceId)!,
        target: nodeIndexById.get(aggregate.targetId)!,
        kind: kinds.length === 1 ? kinds[0]! : "aggregated",
        raw,
      };
    });

  return {
    graph: assembleGraph(nodes, edges),
    analysisGraph,
    memberIdsByGroup,
    sourceToGroupId,
  };
}

const FAMILY_ID_KEYS = [
  "family_id",
  "artifact_family_id",
  "operation_family_id",
  "logical_id",
  "logical_artifact_id",
  "artifact_id",
  "record_family_id",
  "result_family_id",
  "run_family_id",
  "result_family",
  "family",
  "tsangpo_family",
] as const;

const RUN_ID_KEYS = [
  "run_id",
  "runId",
  "operation_run_id",
  "operation_id",
  "execution_id",
  "revision_id",
  "version_id",
  "artifact_version_id",
  "flow360_case_id",
  "case_id",
  "commit_id",
] as const;

const FAMILY_LABEL_KEYS = ["family_label", "artifact_family_label", "logical_name", "family_name"] as const;
const RUN_LABEL_KEYS = ["run_label", "run_name", "revision_label", "version_label", "case_name"] as const;
const SWEEP_ID_KEYS = ["sweep_id", "sweep_group", "campaign_id", "tsangpo_campaign_id"] as const;
const PARAMETER_KEYS = ["parameters", "params", "sweep_params", "sweep_parameters", "config_params"] as const;

function extractParameters(sources: ReadonlyArray<ValueSource>): Readonly<Record<string, unknown>> {
  const parameters: Record<string, unknown> = {};
  for (const source of sources) {
    for (const key of PARAMETER_KEYS) {
      const value = source.value[key];
      if (Array.isArray(value)) {
        if (!(key in parameters)) parameters[key] = [...value];
      } else if (value && typeof value === "object") {
        for (const [parameter, parameterValue] of Object.entries(value as Record<string, unknown>)) {
          if (!(parameter in parameters)) parameters[parameter] = parameterValue;
        }
      }
    }
  }
  return parameters;
}

function cellId(familyId: string, runId: string): string {
  return `run-cell:${safeKey(familyId)}:${safeKey(runId)}`;
}

function timestampMillis(timestamp: string | null): number {
  if (!timestamp) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

export function buildRunMatrixModel(graph: DagViewerGraph): DagRunMatrixModel {
  const records: DagRunMatrixRecord[] = [];
  const excludedNodeIds: string[] = [];
  let nodesWithFamily = 0;
  let nodesWithRun = 0;
  let missingFamily = 0;
  let missingRun = 0;
  let missingBoth = 0;

  graph.nodes.forEach((node, sourceIndex) => {
    const sources = valueSources(node);
    const family = findField(sources, FAMILY_ID_KEYS);
    const run = findField(sources, RUN_ID_KEYS);
    if (family) nodesWithFamily += 1;
    if (run) nodesWithRun += 1;
    if (!family || !run) {
      excludedNodeIds.push(node.id);
      if (!family && !run) missingBoth += 1;
      else if (!family) missingFamily += 1;
      else missingRun += 1;
      return;
    }
    records.push({
      nodeId: node.id,
      sourceIndex,
      familyId: family.value,
      familyLabel: findField(sources, FAMILY_LABEL_KEYS)?.value ?? family.value,
      familySource: family.source,
      runId: run.value,
      runLabel: findField(sources, RUN_LABEL_KEYS)?.value ?? run.value,
      runSource: run.source,
      sweepId: findField(sources, SWEEP_ID_KEYS)?.value ?? null,
      state: node.state,
      timestamp: node.timestamp,
      parameters: extractParameters(sources),
    });
  });

  records.sort((a, b) =>
    a.familyId.localeCompare(b.familyId)
    || timestampMillis(a.timestamp) - timestampMillis(b.timestamp)
    || a.runId.localeCompare(b.runId)
    || a.nodeId.localeCompare(b.nodeId));
  excludedNodeIds.sort((a, b) => a.localeCompare(b));

  const cellById = new Map<string, DagRunMatrixCell>();
  for (const record of records) {
    const id = cellId(record.familyId, record.runId);
    let cell = cellById.get(id);
    if (!cell) {
      cell = { id, familyId: record.familyId, runId: record.runId, records: [], nodeIds: [], states: [], state: null };
      cellById.set(id, cell);
    }
    cell.records.push(record);
    cell.nodeIds.push(record.nodeId);
  }
  for (const cell of cellById.values()) {
    cell.nodeIds.sort((a, b) => a.localeCompare(b));
    cell.states = [...new Set(cell.records.map((record) => record.state).filter((state): state is string => state != null))]
      .sort((a, b) => a.localeCompare(b));
    cell.state = cell.states.length === 1 ? cell.states[0]! : null;
  }

  const familyRollups = new Map<string, { labels: Set<string>; cells: DagRunMatrixCell[]; recordCount: number }>();
  const runRollups = new Map<string, { labels: Set<string>; timestamp: string | null; recordCount: number }>();
  for (const record of records) {
    let family = familyRollups.get(record.familyId);
    if (!family) {
      family = { labels: new Set(), cells: [], recordCount: 0 };
      familyRollups.set(record.familyId, family);
    }
    family.labels.add(record.familyLabel);
    family.recordCount += 1;

    let run = runRollups.get(record.runId);
    if (!run) {
      run = { labels: new Set(), timestamp: null, recordCount: 0 };
      runRollups.set(record.runId, run);
    }
    run.labels.add(record.runLabel);
    run.recordCount += 1;
    if (record.timestamp && (
      run.timestamp == null
      || timestampMillis(record.timestamp) < timestampMillis(run.timestamp)
      || (timestampMillis(record.timestamp) === timestampMillis(run.timestamp) && record.timestamp.localeCompare(run.timestamp) < 0)
    )) run.timestamp = record.timestamp;
  }
  for (const cell of cellById.values()) familyRollups.get(cell.familyId)!.cells.push(cell);

  const rows = [...familyRollups.entries()].map(([familyId, family]): DagRunMatrixRow => {
    const labels = [...family.labels].sort((a, b) => a.localeCompare(b));
    family.cells.sort((a, b) => a.runId.localeCompare(b.runId));
    return { id: familyId, label: labels[0] ?? familyId, labels, cells: family.cells, recordCount: family.recordCount };
  }).sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));

  const columns = [...runRollups.entries()].map(([runId, run]): DagRunMatrixColumn => {
    const labels = [...run.labels].sort((a, b) => a.localeCompare(b));
    return { id: runId, label: labels[0] ?? runId, labels, timestamp: run.timestamp, recordCount: run.recordCount };
  }).sort((a, b) =>
    timestampMillis(a.timestamp) - timestampMillis(b.timestamp)
    || a.label.localeCompare(b.label)
    || a.id.localeCompare(b.id));

  const totalNodes = graph.nodes.length;
  const includedNodes = records.length;
  return {
    rows,
    columns,
    records,
    cellById,
    excludedNodeIds,
    coverage: {
      totalNodes,
      includedNodes,
      excludedNodes: totalNodes - includedNodes,
      ratio: totalNodes ? includedNodes / totalNodes : 0,
      nodesWithFamily,
      nodesWithRun,
      missingFamily,
      missingRun,
      missingBoth,
    },
  };
}

const EVENT_TYPE_KEYS = ["event_type", "eventType", "action", "change_type", "lifecycle_event"] as const;
const EVENT_LABEL_KEYS = ["event_label", "summary", "event_summary"] as const;
const ACTOR_KEYS = ["actor_name", "actor", "created_by_name", "created_by", "actor_id", "user_id", "engineer_id"] as const;
const OCCURRED_AT_KEYS = ["occurred_at", "event_at", "effective_at"] as const;
const RECORDED_AT_KEYS = ["recorded_at", "created_at", "captured_at", "ingested_at"] as const;

function validTimestamp(match: FieldMatch | null): FieldMatch | null {
  if (!match) return null;
  return Number.isFinite(Date.parse(match.value)) ? match : null;
}

function humanizeEventType(value: string): string {
  const spaced = value.replace(/[._-]+/g, " ").replace(/\s+/g, " ").trim();
  return spaced ? `${spaced.charAt(0).toLocaleUpperCase()}${spaced.slice(1)}` : "Event";
}

function makeAuditEvent(
  node: DagViewerNode,
  sourceIndex: number,
  timestamp: FieldMatch,
  timeSemantics: DagAuditTimeSemantics,
  eventType: string | null,
  eventLabel: string | null,
  actor: string | null,
  suffix: string,
): DagAuditEvent {
  const subject = eventLabel ?? (eventType ? humanizeEventType(eventType) : node.label);
  return {
    id: `audit:${safeKey(node.id)}:${suffix}`,
    nodeId: node.id,
    sourceIndex,
    timestamp: timestamp.value,
    timestampSource: timestamp.source,
    timeSemantics,
    eventType,
    label: timeSemantics === "recorded" ? `Recorded · ${subject}` : subject,
    actor,
    state: node.state,
  };
}

export function buildAuditModel(graph: DagViewerGraph): DagAuditModel {
  const events: DagAuditEvent[] = [];
  const untimedNodeIds: string[] = [];
  const invalidTimestampNodeIds: string[] = [];
  let eventTimeNodes = 0;
  let recordedTimeNodes = 0;

  graph.nodes.forEach((node, sourceIndex) => {
    const sources = valueSources(node);
    const eventType = findField(sources, EVENT_TYPE_KEYS)?.value ?? null;
    const eventLabel = findField(sources, EVENT_LABEL_KEYS)?.value ?? null;
    const actor = findField(sources, ACTOR_KEYS)?.value ?? null;
    const timestampCandidates: FieldMatch[] = [];
    for (const key of [...OCCURRED_AT_KEYS, "timestamp", "t", "started_at", "completed_at", ...RECORDED_AT_KEYS, "updated_at"]) {
      const match = findField(sources, [key]);
      if (match) timestampCandidates.push(match);
    }

    const semanticEvents: DagAuditEvent[] = [];
    const occurrence = validTimestamp(findField(sources, OCCURRED_AT_KEYS));
    if (occurrence) {
      semanticEvents.push(makeAuditEvent(
        node,
        sourceIndex,
        occurrence,
        "event",
        eventType,
        eventLabel,
        actor,
        eventType ? safeKey(eventType) : "event",
      ));
    } else if (eventType) {
      const genericTimestamp = validTimestamp(findField(sources, ["timestamp", "t"]));
      if (genericTimestamp) {
        semanticEvents.push(makeAuditEvent(
          node,
          sourceIndex,
          genericTimestamp,
          "event",
          eventType,
          eventLabel,
          actor,
          safeKey(eventType),
        ));
      }
    }

    if (node.type === "operation") {
      const startedAt = validTimestamp(findField(sources, ["started_at"]));
      const completedAt = validTimestamp(findField(sources, ["completed_at"]));
      if (startedAt) {
        semanticEvents.push(makeAuditEvent(
          node,
          sourceIndex,
          startedAt,
          "event",
          "operation_started",
          `Started · ${node.label}`,
          actor,
          "operation-started",
        ));
      }
      if (completedAt) {
        semanticEvents.push(makeAuditEvent(
          node,
          sourceIndex,
          completedAt,
          "event",
          "operation_completed",
          `Completed · ${node.label}`,
          actor,
          "operation-completed",
        ));
      }
    }

    if (semanticEvents.length) {
      const unique = new Map(semanticEvents.map((event) => [`${event.timestamp}\u0000${event.eventType ?? ""}`, event]));
      events.push(...unique.values());
      eventTimeNodes += 1;
      return;
    }

    const recordedAt = validTimestamp(findField(sources, RECORDED_AT_KEYS))
      ?? validTimestamp(findField(sources, ["updated_at"]))
      ?? validTimestamp(findField(sources, ["timestamp", "t"]))
      ?? (node.timestamp && Number.isFinite(Date.parse(node.timestamp))
        ? { value: node.timestamp, source: "normalized.timestamp" }
        : null);
    if (recordedAt) {
      events.push(makeAuditEvent(
        node,
        sourceIndex,
        recordedAt,
        "recorded",
        eventType,
        eventLabel,
        actor,
        "recorded",
      ));
      recordedTimeNodes += 1;
      return;
    }

    if (timestampCandidates.length) invalidTimestampNodeIds.push(node.id);
    else untimedNodeIds.push(node.id);
  });

  events.sort((a, b) =>
    Date.parse(b.timestamp) - Date.parse(a.timestamp)
    || a.id.localeCompare(b.id));
  untimedNodeIds.sort((a, b) => a.localeCompare(b));
  invalidTimestampNodeIds.sort((a, b) => a.localeCompare(b));

  const totalNodes = graph.nodes.length;
  const includedNodes = eventTimeNodes + recordedTimeNodes;
  return {
    events,
    untimedNodeIds,
    invalidTimestampNodeIds,
    coverage: {
      totalNodes,
      includedNodes,
      excludedNodes: totalNodes - includedNodes,
      ratio: totalNodes ? includedNodes / totalNodes : 0,
      eventTimeNodes,
      recordedTimeNodes,
      invalidTimestampNodes: invalidTimestampNodeIds.length,
      eventEntries: events.filter((event) => event.timeSemantics === "event").length,
      recordedEntries: events.filter((event) => event.timeSemantics === "recorded").length,
    },
  };
}
